import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { FormBroadcast, FormComponentLookup, FormEntryComponentClass } from '@ptl/forms';
import { DateService } from 'src/app/shared/services/date.service';
import { FamilyFormService } from '../../family-form/services/family-form.service';
import { GetOpenEnrollmentRule } from '../../family-form/types/qualifying-life-event.response.interface';

interface ComponentFormControls {
  lifeEvent: AbstractControl;
}
@FormComponentLookup('special-enrollment-event-selector')
@Component({
    selector: 'app-special-enrollment-event-selector',
    templateUrl: './special-enrollment-event-selector.component.html',
    styleUrls: ['./special-enrollment-event-selector.component.scss'],
    standalone: false
})
export class SpecialEnrollmentEventSelectorComponent extends FormEntryComponentClass implements OnInit, AfterViewInit, OnDestroy {

  qualifyingLifeEvents: GetOpenEnrollmentRule[] = [];

  constructor(
    private familyFormService: FamilyFormService,
    private dateService: DateService
  ) {
    super();
  }
  async ngOnInit(): Promise<void> {
    await this.setupOptions();
  }
  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.broadcastCurrentValue();
  }
  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  get componentFormControls(): ComponentFormControls {
    return this.formControls as unknown as ComponentFormControls;
  }

  /** Necessary to have VIEW reflect PROGRAMMATIC setting of values */
  compareWithFn(obj1: GetOpenEnrollmentRule, obj2: GetOpenEnrollmentRule) {
    return obj1 && obj2 && obj1.lifeEventId === obj2.lifeEventId;
  }

  /**
   * This broadcast and set timeout allows the date-of-life-event component and the page components to see this value:
   *  a) on change ( user interaction )
   *  b) on load ( page navigation / refresh ) 
   * such that the submit button can base its existence on this value
   * [ the page component doesn't appear to have much in the way of conditional on-load comparison, so this aligns with on-change comparisons]
   * 
   * It also sends just ONE string value instead of an entire object ( due to the pain to observe a specific attribute on an object being passed around via the formListeners )
   */
  broadcastCurrentValue(lifeEvent?: GetOpenEnrollmentRule) {
    setTimeout(() => {
      if (!lifeEvent) {
        /** Component form's lifeEvent won't exist until the component's view has loaded */
        lifeEvent = this.componentFormControls.lifeEvent?.value;
      }

      if (lifeEvent) {

        const evt: FormBroadcast = {
          formControlName: 'lifeEventId',
          type: 'valueChange',
          value: lifeEvent.lifeEventId
        };
        this.formStreamTrigger.triggerFormStream(evt);
      }
    }, 100);
  }

  selectionChange(event: GetOpenEnrollmentRule) {
    this.broadcastCurrentValue(event);
  }

  async setupOptions() {
    if (this.entry?.custom?.qualifyingLifeEvents) {

      const today = this.dateService.getTodayAsMoment();
      const todayMonth = today.month() + 1;
      const todayDate = today.date();
      const todayYear = today.year();
      const asOfToday = `${todayYear}-${todayMonth}-${todayDate}`;

      this.qualifyingLifeEvents
        = (this.entry.custom.qualifyingLifeEvents as GetOpenEnrollmentRule[]).map(event => {
          event.asOfDate = asOfToday;
          return event;
        });
    } else {
      /** Get qualifying life event options */
      const response = await this.familyFormService.getOpenEnrollmentRules(this.entry?.custom?.lifeEventObj?.lifeEventId);
      this.qualifyingLifeEvents = response.responseData.dataElement.getOpenEnrollmentRules;
    }
  }
}
